import { ChangeDetectorRef, Component, Inject, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatAutocompleteModule, MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { MatButtonModule } from '@angular/material/button';
import { DateAdapter } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { DatePickerComponent } from '@shared/components/date-picker/date-picker.component';
import { MemberLinkSourceComponent } from '@shared/components/member-link-source/member-link-source.component';
import { of } from 'rxjs';
import { catchError, finalize } from 'rxjs/operators';
import { PersonLookupOption } from '../models/person-event.model';
import { PersonRelationshipItem, PersonService } from '../services/person.service';

export interface FamilyRelationshipDialogData {
  title: string;
  item?: PersonRelationshipItem;
  showValidFrom: boolean;
}

@Component({
  selector: 'app-family-relationship-edit-dialog',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, MatDialogModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatIconModule, MatAutocompleteModule, MatProgressSpinnerModule, DatePickerComponent, MemberLinkSourceComponent],
  template: `
    <h2 mat-dialog-title>{{ data.title }}</h2>
    <form [formGroup]="form" (ngSubmit)="save()">
      <mat-dialog-content>
        <mat-form-field>
          <mat-label>Nombre</mat-label>
          <input matInput formControlName="relatedPersonName" [matAutocomplete]="members" required (input)="search($any($event.target).value)">
          <mat-spinner matSuffix diameter="18" *ngIf="searching"></mat-spinner>
          <mat-autocomplete #members="matAutocomplete">
            <mat-option *ngFor="let option of options" [value]="option.name" (onSelectionChange)="$event.isUserInput && select(option)">
              {{ option.name }}<small *ngIf="option.subtitle"> · {{ option.subtitle }}</small>
            </mat-option>
          </mat-autocomplete>
        </mat-form-field>
        <app-member-link-source
          *ngIf="form.controls.relatedPersonName.value"
          [personId]="form.controls.relatedPersonId.value">
        </app-member-link-source>
        <app-date-picker *ngIf="data.showValidFrom" label="Vínculo desde" formControlName="validFrom"></app-date-picker>
        <mat-form-field>
          <mat-label>Observaciones</mat-label>
          <input matInput formControlName="notes">
        </mat-form-field>
      </mat-dialog-content>
      <mat-dialog-actions align="end">
        <button mat-button type="button" (click)="ref.close()">Cancelar</button>
        <button mat-flat-button color="primary" type="submit" [disabled]="form.invalid">Guardar</button>
      </mat-dialog-actions>
    </form>`,
  styles: [`
    mat-form-field { display: block; width: 100%; }
    mat-dialog-content { min-width: 360px; }
    small { color: #667085; }
  `],
})
export class FamilyRelationshipEditDialogComponent {
  readonly form;
  options: PersonLookupOption[] = [];
  searching = false;
  private searchRequestId = 0;

  @ViewChild(MatAutocompleteTrigger) private autocompleteTrigger?: MatAutocompleteTrigger;

  constructor(
    private readonly fb: FormBuilder,
    private readonly dateAdapter: DateAdapter<any>,
    private readonly people: PersonService,
    private readonly cdr: ChangeDetectorRef,
    public readonly ref: MatDialogRef<FamilyRelationshipEditDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: FamilyRelationshipDialogData,
  ) {
    this.form = fb.group({
      relatedPersonId: [data.item?.relatedPersonId ?? null],
      relatedPersonName: [data.item?.relatedPersonName ?? ''],
      validFrom: [this.fromIsoDate(data.item?.validFrom)],
      notes: [data.item?.notes ?? ''],
    });
  }

  search(value: string): void {
    const term = value.trim();
    const requestId = ++this.searchRequestId;
    this.form.controls.relatedPersonId.setValue(null, { emitEvent: false });

    if (term.length < 2) {
      this.options = [];
      this.searching = false;
      this.cdr.markForCheck();
      return;
    }

    this.searching = true;
    this.cdr.markForCheck();
    this.people.searchPeople(term).pipe(
      catchError(() => of([])),
      finalize(() => {
        if (requestId === this.searchRequestId) {
          this.searching = false;
          this.cdr.markForCheck();
        }
      }),
    ).subscribe(items => {
      if (requestId !== this.searchRequestId) {
        return;
      }

      this.options = items;
      this.cdr.markForCheck();
      if (items.length) {
        queueMicrotask(() => this.autocompleteTrigger?.openPanel());
      }
    });
  }

  select(option: PersonLookupOption): void {
    this.form.patchValue({ relatedPersonId: option.id, relatedPersonName: option.name });
    this.options = [];
  }

  save(): void {
    if (this.form.valid) {
      const value = this.form.getRawValue();
      this.ref.close({ ...value, validFrom: this.toIsoDate(value.validFrom) });
    }
  }

  private toIsoDate(value: any): string | null {
    if (!value) return null;
    const year = this.dateAdapter.getYear(value);
    const month = this.dateAdapter.getMonth(value) + 1;
    const day = this.dateAdapter.getDate(value);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  }

  private fromIsoDate(value?: string | null): any | null {
    if (!value) return null;
    const [year, month, day] = value.split('-').map(Number);
    return year && month && day ? this.dateAdapter.createDate(year, month - 1, day) : null;
  }
}
